import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

interface CreateRFPProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const projectTypes = [
  'Forest Conservation',
  'Renewable Energy',
  'Methane Reduction',
  'Blue Carbon',
  'Soil Carbon',
  'Direct Air Capture',
  'Biomass Energy',
  'Energy Efficiency',
  'Transportation',
  'Waste Management'
];

export const CreateRFP: React.FC<CreateRFPProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    project_type: '',
    description: '',
    location: '',
    estimated_volume: '',
    timeline: ''
  });
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const { data: rfpInsert, error } = await supabase
        .from('rfps')
        .insert({
          created_by: user.id,
          title: formData.title,
          project_type: formData.project_type,
          description: formData.description,
          location: formData.location,
          estimated_volume: formData.estimated_volume ? Number(formData.estimated_volume) : null,
          timeline: formData.timeline,
          status: 'open'
        })
        .select('id')
        .single();

      if (error) throw error;

      toast({
        title: "RFP Created Successfully",
        description: "Your request for proposal has been published and project developers will be notified.",
      });

      // Reset form
      setFormData({
        title: '',
        project_type: '',
        description: '',
        location: '',
        estimated_volume: '',
        timeline: ''
      });

      // Notify all project developers about new RFP
      if (rfpInsert?.id) {
        await supabase.rpc('notify_project_developers_on_rfp', {
          rfp_id_input: rfpInsert.id,
          title_input: 'New RFP Posted',
          message_input: `${formData.title} – ${formData.project_type}`
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error creating RFP:', error);
      toast({
        title: "Error",
        description: "Failed to create RFP. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Request for Proposal</DialogTitle>
          <DialogDescription>
            Submit a request for proposal to connect with qualified project developers
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Solar Farm Development in Texas"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project_type">Project Type *</Label>
              <Select value={formData.project_type} onValueChange={(value) => handleInputChange('project_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Project Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Provide a detailed description of your carbon credit project requirements..."
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location">Desired Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., Texas, USA or Global"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimated_volume">Estimated Credit Volume</Label>
              <Input
                id="estimated_volume"
                type="number"
                value={formData.estimated_volume}
                onChange={(e) => handleInputChange('estimated_volume', e.target.value)}
                placeholder="e.g., 10000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeline">Timeline</Label>
            <Input
              id="timeline"
              value={formData.timeline}
              onChange={(e) => handleInputChange('timeline', e.target.value)}
              placeholder="e.g., 6-12 months"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create RFP'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};